import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';
import Axios from 'axios';
import merge from 'lodash/merge';

/**
 * This abstract class is intended to act as the base class for all specific
 * HTTP client implementations provided by this library.
 *
 * This class implements the the `HttpClient`, thus all other concrete HTTP client
 * implementations that derive from it also end up adhering to this API interface,
 * which is a key design goal for this library.
 */
var DecoratedHttpClient = /*#__PURE__*/function () {
  /**
   * The internal HttpClient instance that is being decorated.
   */

  /**
   * Concrete classes should set default Axios config specific to their
   * implementation in to this property.
   *
   * They should then call `this.mergeDefaults()` in their constructor to safely
   * merge these with those on the httpClient (Axios instance).
   */

  /**
   * Concrete classes should use this property to add any custom interceptors
   * specific to their implementation, in their constructor code.
   */

  /**
   * This constructor will be inherited by any concrete classes extending this
   * class.
   *
   * However, most concrete classes would need to provide their own constructor
   * in order to implement custom logic for constructing the Axios instance in
   * the desired manner.
   *
   * @param httpClient If passed, this Axios instance will be used instead of
   * creating one.
   */
  function DecoratedHttpClient(httpClient) {
    _classCallCheck(this, DecoratedHttpClient);

    this.httpClient = void 0;
    this.defaults = {};
    this.interceptors = void 0;
    this.httpClient = httpClient !== null && httpClient !== void 0 ? httpClient : Axios.create();
    this.mergeDefaults();
    this.interceptors = this.httpClient.interceptors;
  }
  /**
   * This is a helper function that safely merges concrete class specific
   * config defaults with those on the httpClient (Axios instance).
   *
   * Merging own config defaults with those on the httpClient (Axios instance)
   * must be done using deep merge otherwise Axios does not seem to honor the
   * overrides.
   *
   * Also, the result must also be assigned to the concrete class `defaults`
   * property in order for chains of decorated concrete classes to also work
   * properly.
   */


  _createClass(DecoratedHttpClient, [{
    key: "mergeDefaults",
    value: function mergeDefaults() {
      this.defaults = merge(this.httpClient.defaults, this.defaults);
    }
    /**
     * Get a fully formed URI with with an HTTP request will be sent. This is based
     * on the `config` passed as well the HttpClient instance `defaults`.
     *
     * @param config AxiosRequestConfig object.
     */

  }, {
    key: "getUri",
    value: function getUri(config) {
      return this.httpClient.getUri(config);
    }
    /**
     * Sends an HTTP request as per the `config` passed.
     *
     * @param config AxiosRequestConfig object. If passed, it is merged with the
     * `defaults` on the HttpClient instance. If not passed, then simply uses the
     * `defaults.`
     */

  }, {
    key: "request",
    value: function request(config) {
      return this.httpClient.request(config);
    }
    /**
     * Sends an HTTP GET request as per the `config` passed.
     *
     * @param url The URL to call.
     * @param config AxiosRequestConfig object.
     */

  }, {
    key: "get",
    value: function get(url) {
      var config = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var ownConfig = {
        url: url,
        method: "get"
      };
      var requestConfig = merge(config, ownConfig);
      return this.request(requestConfig);
    }
    /**
     * Sends an HTTP HEAD request as per the `config` passed.
     *
     * @param url The URL to call.
     * @param config AxiosRequestConfig object.
     */

  }, {
    key: "head",
    value: function head(url) {
      var config = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var ownConfig = {
        url: url,
        method: "head"
      };
      var requestConfig = merge(config, ownConfig);
      return this.request(requestConfig);
    }
    /**
     * Sends an HTTP OPTIONS request as per the `config` passed.
     *
     * @param url The URL to call.
     * @param config AxiosRequestConfig object.
     */

  }, {
    key: "options",
    value: function options(url) {
      var config = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var ownConfig = {
        url: url,
        method: "options"
      };
      var requestConfig = merge(config, ownConfig);
      return this.request(requestConfig);
    }
    /**
     * Sends an HTTP DELETE request as per the `config` passed.
     *
     * @param url The URL to call.
     * @param data Request data object.
     * @param config AxiosRequestConfig object.
     */

  }, {
    key: "delete",
    value: function _delete(url, data) {
      var config = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      var ownConfig = {
        url: url,
        method: "delete",
        data: data
      };
      var requestConfig = merge(config, ownConfig);
      return this.request(requestConfig);
    }
    /**
     * Sends an HTTP POST request as per the `config` passed.
     *
     * @param url The URL to call.
     * @param data Request data object.
     * @param config AxiosRequestConfig object.
     */

  }, {
    key: "post",
    value: function post(url, data) {
      var config = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      var ownConfig = {
        url: url,
        method: "post",
        data: data
      };
      var requestConfig = merge(config, ownConfig);
      return this.request(requestConfig);
    }
    /**
     * Sends an HTTP PUT request as per the `config` passed.
     *
     * @param url The URL to call.
     * @param data Request data object.
     * @param config AxiosRequestConfig object.
     */

  }, {
    key: "put",
    value: function put(url, data) {
      var config = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      var ownConfig = {
        url: url,
        method: "put",
        data: data
      };
      var requestConfig = merge(config, ownConfig);
      return this.request(requestConfig);
    }
    /**
     * Sends an HTTP PATCH request as per the `config` passed.
     *
     * @param url The URL to call.
     * @param data Request data object.
     * @param config AxiosRequestConfig object.
     */

  }, {
    key: "patch",
    value: function patch(url, data) {
      var config = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      var ownConfig = {
        url: url,
        method: "patch",
        data: data
      };
      var requestConfig = merge(config, ownConfig);
      return this.request(requestConfig);
    }
  }]);

  return DecoratedHttpClient;
}();

export { DecoratedHttpClient };
//# sourceMappingURL=decorated-http-client.js.map
